import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { listProfiles } from '../services/profilesService';

const ProfileContext = createContext();

// Per-account localStorage key so a stored selection never leaks across
// different Manager accounts on the same device.
const storageKey = (ownerUserId) => `pumpous.activeProfile.${ownerUserId}`;

// Pick the profile to make active: prefer a still-valid stored selection,
// otherwise fall back to the oldest non-archived profile (list is oldest-first).
const resolveActiveProfile = (profiles, storedId) => {
  const selectable = profiles.filter((p) => p.isActive !== false);
  if (selectable.length === 0) return null;

  if (storedId) {
    const stored = selectable.find((p) => p.id === storedId);
    if (stored) return stored;
  }

  return selectable[0];
};

export const ProfileProvider = ({ ownerUserId, children }) => {
  const [profiles, setProfiles] = useState([]);
  const [activeProfile, setActiveProfile] = useState(null);
  const [loading, setLoading] = useState(false);

  // Fetch profiles for the current owner and restore the active selection.
  const loadProfiles = useCallback(async () => {
    if (!ownerUserId) {
      setProfiles([]);
      setActiveProfile(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    const result = await listProfiles(ownerUserId);
    const nextProfiles = result.success ? result.profiles : [];
    setProfiles(nextProfiles);

    let storedId = null;
    try {
      storedId = localStorage.getItem(storageKey(ownerUserId));
    } catch (e) {
      // localStorage may be unavailable (private mode / SSR); degrade gracefully.
      storedId = null;
    }

    setActiveProfile(resolveActiveProfile(nextProfiles, storedId));
    setLoading(false);
  }, [ownerUserId]);

  useEffect(() => {
    loadProfiles();
  }, [loadProfiles]);

  // Select a loaded profile by id and persist the choice for this owner.
  const selectProfile = (profileId) => {
    const target = profiles.find((p) => p.id === profileId);
    if (!target) return;

    setActiveProfile(target);
    if (ownerUserId) {
      try {
        localStorage.setItem(storageKey(ownerUserId), profileId);
      } catch (e) {
        // Ignore persistence failures; selection still holds in memory.
      }
    }
  };

  // Re-fetch the profile list (e.g. after create/archive) without a remount.
  const refreshProfiles = () => loadProfiles();

  return (
    <ProfileContext.Provider
      value={{ profiles, activeProfile, loading, selectProfile, refreshProfiles }}
    >
      {children}
    </ProfileContext.Provider>
  );
};

export const useProfile = () => useContext(ProfileContext);
